
import React from 'react';
import { CircleDecoration } from '../Animations';
import { Calendar, Check, Users, BrainCircuit } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import OptimizedFadeIn from '../OptimizedFadeIn';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  delay
}) => (
  <OptimizedFadeIn className="h-full">
    <Card className="bg-white border-0 rounded-3xl shadow-sm hover:shadow-md transition-all overflow-hidden h-full">
      <div className="h-1 bg-[#E27D60]"></div>
      <CardContent className="p-6">
        <div className="w-14 h-14 rounded-full bg-[#E27D60]/10 flex items-center justify-center mb-4">
          {icon}
        </div>
        <h3 className="text-xl font-semibold text-hormony-charcoal mb-2">{title}</h3>
        <p className="text-hormony-charcoal/70">{description}</p>
      </CardContent>
    </Card>
  </OptimizedFadeIn>
);

// Pre-define features to avoid re-creating on each render
const features = [
  {
    icon: <Calendar className="text-[#E27D60]" size={24} />,
    title: "Daily 3-Minute Videos",
    description: "Simple, engaging lessons on hormones & wellness that fit into your busy schedule.",
    delay: 100
  },
  {
    icon: <Check className="text-[#E27D60]" size={24} />,
    title: "Personalized Tracking",
    description: "Stay motivated with interactive progress tracking and personalized insights.",
    delay: 200
  },
  {
    icon: <Users className="text-[#E27D60]" size={24} />,
    title: "Community Support",
    description: "A private space to connect with other women on the same journey, share experiences, and grow together.",
    delay: 300
  },
  {
    icon: <BrainCircuit className="text-[#E27D60]" size={24} />,
    title: "Science-Based Habits",
    description: "Tiny, proven steps based on behavioral science that lead to lasting hormonal health improvements.",
    delay: 400
  }
];

const FeaturesSection: React.FC = () => {
  return (
    <section className="bg-[#F9F5F0] relative py-[50px]">
      <CircleDecoration size={200} color="rgba(226, 125, 96, 0.07)" className="absolute bottom-20 right-10 z-0" />
      
      <div className="container mx-auto px-4 relative z-10">
        <OptimizedFadeIn>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-display font-semibold text-hormony-charcoal mb-4">
              What You'll Get in 30 Days
            </h2>
            <p className="text-lg text-hormony-charcoal/70">
              A guided journey designed to optimize your hormonal health with expert content and daily practices.
            </p>
          </div>
        </OptimizedFadeIn>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <FeatureCard 
              key={index} 
              icon={feature.icon} 
              title={feature.title} 
              description={feature.description} 
              delay={feature.delay} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default React.memo(FeaturesSection);
